import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Emergent Google OAuth callback handler.
 * Reads #session_id=... from the URL hash (populated by the Emergent auth service
 * after Google consent), exchanges it for our JWT via /api/auth/google/session,
 * then hands off to AuthContext + navigates to /dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(match[1]);

    (async () => {
      try {
        const r = await api.post("/auth/google/session", { session_id });
        login(r.data.user);
        // Clear the hash so a refresh doesn't retry
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(r.data.is_new ? `Welcome to Verda, ${r.data.user.name}!` : `Welcome back, ${r.data.user.name}`);
        navigate("/dashboard", { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || "Google sign-in failed";
        toast.error(detail);
        navigate("/", { replace: true });
      }
    })();
  }, [location.hash, login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]" data-testid="auth-callback">
      <div className="flex items-center gap-3 text-[#4A6750]">
        <div className="w-6 h-6 rounded-full border-2 border-[#4A6750] border-t-transparent animate-spin" />
        <span className="font-medium">Signing you in…</span>
      </div>
    </div>
  );
}
